import { Box, Divider, Grid } from '@mui/material'
import { ReactNode, useState } from 'react'
import Popup, { PopupInstance } from 'components/popup/Popup'
import { EventDoc, EventReminder } from 'types/api/changes'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { CustomReminderContent } from './CustomEventRecurrencePopup'
import { t } from 'i18next'
import i18n from 'i18n'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { isAMPM } from 'utils/date'
import { isTrulyEmpty } from 'utils/object'
import { dateInTimezone, getDefaultTimezone } from 'utils/timezone'
import { allDayReminderTime } from 'utils/calendarTimezone'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

dayjs.extend(utc)
dayjs.extend(timezone)

interface ContentProps {
  instance: PopupInstance | null | undefined
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  event?: EventDoc
}

const humanizeDuration = require('humanize-duration')

export function convertMinutesToSeconds(minutes: number) {
  return minutes * 60
}

function getReminderTitle(
  reminder: EventReminder,
  tz: string = getDefaultTimezone()
): string {
  const hour = dayjs(Number(reminder.AlertTime) * 1000)
    .tz(tz)
    .format(isAMPM() ? 'h A' : 'H:mm')

  if (reminder.offset > 0) {
    return `${t('timeBefore', {
      time: minutesToHumanReadable(reminder.offset / 60)
    })} (${hour})`
  }

  return t('onDayOfEvent', {
    time: hour
  })
}

function Content(props: ContentProps) {
  const addReminderToEvent = (reminder: EventReminder) => {
    if (
      props.reminders == undefined ||
      !props.reminders.find((rem) => rem.offset === reminder.offset)
    ) {
      props.setReminders([...(props.reminders ?? []), reminder])
    }

    props.instance?.hide()
  }

  const [showCustom, setShowCustom] = useState(false)

  const allday = props.event?.AllDay === '1'
  const eventStart = props.event?.StartDate // seconds since epoch (midnight UTC for all-day events)

  // Helper function to create 9 AM reminder in user's timezone
  const create9AMReminder = (daysOffset: number = 0): EventReminder | null => {
    if (!eventStart) return null

    const nineAmUserTz = allDayReminderTime(eventStart, daysOffset)

    const offset =
      daysOffset === 0
        ? -nineAmUserTz.utcOffset() * 60
        : convertMinutesToSeconds(daysOffset * 24 * 60)

    return {
      AlertTime: String(nineAmUserTz.unix()),
      isOn: '1',
      isRelativeReminder: '1',
      offset
    }
  }

  // For all-day events, StartTime is midnight (00:00 UTC) of the event day in seconds
  // We calculate AlertTime as 9 AM in the user's timezone for each day
  const allDayReminders: EventReminder[] = [
    create9AMReminder(0),
    create9AMReminder(1),
    create9AMReminder(2),
    create9AMReminder(7)
  ].filter((r): r is EventReminder => r !== null)

  const defaultReminders: EventReminder[] = [
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: 0
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(5)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(15)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(30)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(60)
    }
  ]

  return showCustom ? (
    <CustomReminderContent editingReminder={undefined} {...props} />
  ) : (
    <Grid container alignItems={'start'} width={300}>
      <PopupSection title={`${t('remindMe')}`} noTitleLine>
        <Box height={12}></Box>
        {(allday ? allDayReminders : defaultReminders)
          .filter((n) => {
            if (!props.reminders) {
              return true
            }
            return props.reminders?.includes(n) == false
          })
          .map((rem) => {
            return (
              <PopupSectionRow
                actionBg
                action={() => {
                  addReminderToEvent(rem)
                }}
                actionId={`action-reminder-${rem.offset}`}
                key={rem.offset}
                title={reminderUserTitle(rem, allday)}
              />
            )
          })}
        <div>
          <ReminderDivider />
          <PopupSectionRow
            actionBg
            action={() => {
              setShowCustom(true)
            }}
            title={t('custom')}
          />
        </div>
      </PopupSection>
    </Grid>
  )
}

interface AddReminderPopupProps {
  children: ReactNode
  open: boolean
  setOpen: (b: boolean) => void
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  event?: EventDoc
}

export default function AddReminderPopup(props: AddReminderPopupProps) {
  const [instance, onInstance] = useState<PopupInstance>()

  return (
    <Popup
      onMount={onInstance}
      open={props.open}
      padding={4}
      onHide={() => {
        LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
      }}
      setOpen={props.setOpen}
      content={<Content {...props} instance={instance} />}
      placement={'top'}
    >
      {props.children}
    </Popup>
  )
}

export function minutesToHumanReadable(seconds: number) {
  let minutes = ''

  if (seconds === 0) {
    return t('onTime')
  }

  try {
    minutes = humanizeDuration(seconds * 60 * 1000, {
      language:
        i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
    })
  } catch (error) {
    minutes = humanizeDuration(seconds * 60 * 1000)
  }

  return minutes
}

export function reminderUserTitle(reminder: EventReminder, allDay: boolean) {
  if (
    reminder.isRelativeReminder === '0' &&
    !isTrulyEmpty(reminder.AlertTime) &&
    reminder.offset === 0
  ) {
    const alertTime = dateInTimezone(Number(reminder.AlertTime) * 1000).format(
      isAMPM() ? 'MMM D YYYY h:mm A' : 'MMM D YYYY H:mm'
    )

    return `${t('reminderOn')}: ${alertTime}`
  }

  const displayTitle = allDay
    ? getReminderTitle(reminder, getDefaultTimezone())
    : reminder.offset <= 0
      ? t('onTime')
      : `${t('timeBefore', {
          time: minutesToHumanReadable(reminder.offset / 60)
        })}`
  return displayTitle
}

export function ReminderDivider() {
  return (
    <Divider
      sx={{
        borderColor: 'rgb(187,187,187)',
        marginBottom: 1,
        marginTop: '0.4rem'
      }}
    />
  )
}
